import { ProTable } from '@ant-design/pro-components';
import {
  Alert,
  Button,
  Descriptions,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import { useEffect, useState } from 'react';
import { getAttachmentPreviewUrl } from '@/services/bff/attachment.api';
import {
  getReceiptFinanceLink,
  getRegistration,
  getTransaction,
  getWorkflowPlan,
  queryReceiptRegistrations,
  queryReceiptTransactions,
} from '@/services/bff/receipt-workflow.api';
import type {
  ReceiptPlan,
  ReceiptRegistration,
  ReceiptTransaction,
} from '@/services/bff/types/receipt-workflow';
import { BusinessModal } from '../business-modal';
import { displayReceiptMoney } from './receipt-money';
import { receiptFieldText, receiptStatusText } from './receipt-status';
export function ReceiptDetail({
  kind,
  uuid,
  onClose,
  onEdit,
  onRegister,
}: {
  kind: 'plans' | 'registrations' | 'transactions';
  uuid: string;
  onClose: () => void;
  onEdit?: (record: ReceiptPlan | ReceiptRegistration) => void;
  onRegister?: (plan: ReceiptPlan) => void;
}) {
  const [data, setData] = useState<
    ReceiptPlan | ReceiptRegistration | ReceiptTransaction
  >();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const get =
      kind === 'plans'
        ? getWorkflowPlan
        : kind === 'registrations'
          ? getRegistration
          : getTransaction;
    get(uuid)
      .then(async (result) => {
        if (!active) return;
        setData(result);
      })
      .catch((cause) => {
        if (active)
          setError(cause instanceof Error ? cause.message : '详情加载失败');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [uuid, kind, attempt]);
  const plan = kind === 'plans' ? (data as ReceiptPlan | undefined) : undefined;
  const registration =
    kind === 'registrations'
      ? (data as ReceiptRegistration | undefined)
      : undefined;
  const transaction =
    kind === 'transactions'
      ? (data as ReceiptTransaction | undefined)
      : undefined;
  return (
    <BusinessModal
      open
      title={
        kind === 'plans'
          ? '回款计划详情'
          : kind === 'registrations'
            ? '回款登记详情'
            : '财务回款交易详情'
      }
      widthPreset="detail"
      onCancel={onClose}
      extra={
        <Space>
          {data &&
            'actions' in data &&
            data.actions.edit?.allowed &&
            onEdit && <Button onClick={() => onEdit(data)}>编辑</Button>}
          {plan &&
            ['READY', 'RECOGNIZED'].includes(plan.status) &&
            onRegister && (
              <Button type="primary" onClick={() => onRegister(plan)}>
                发起回款
              </Button>
            )}
        </Space>
      }
    >
      <Space orientation="vertical" style={{ width: '100%' }}>
        {error && (
          <Alert
            type="error"
            title={error}
            action={
              <Button onClick={() => setAttempt((value) => value + 1)}>
                重试
              </Button>
            }
          />
        )}
        <Spin spinning={loading}>
          {data && (
            <>
              <Descriptions
                column={{ xs: 1, sm: 2, lg: 3 }}
                items={[
                  {
                    key: 'contract',
                    label: '合同',
                    children: `${data.contractNo} · ${data.contractName}`,
                  },
                  {
                    key: 'customer',
                    label: '客户',
                    children: data.customerName,
                  },
                  {
                    key: 'status',
                    label: '状态',
                    children: receiptStatusText[data.status] || data.status,
                  },
                  { key: 'currency', label: '币种', children: data.currency },
                  {
                    key: 'amount',
                    label: plan ? '计划金额' : '金额',
                    children: plan
                      ? plan.plannedAmount
                      : registration?.amount || transaction?.amount,
                  },
                  ...(plan
                    ? [
                        {
                          key: 'name',
                          label: '计划名称',
                          children: plan.planName,
                        },
                        {
                          key: 'due',
                          label: '到期日期',
                          children: plan.dueDate || '—',
                        },
                        {
                          key: 'received',
                          label: '已确认金额',
                          children: displayReceiptMoney(
                            plan.financeSettledAmount,
                          ),
                        },
                        {
                          key: 'occupied',
                          label: '待确认占用',
                          children: displayReceiptMoney(plan.occupyingAmount),
                        },
                        {
                          key: 'available',
                          label: '可登记金额',
                          children: displayReceiptMoney(plan.availableAmount),
                        },
                        {
                          key: 'progress',
                          label: '回款进度',
                          children: receiptFieldText('progress', plan.progress),
                        },
                        {
                          key: 'overdue',
                          label: '逾期',
                          children:
                            plan.overdue == null
                              ? '待同步'
                              : plan.overdue
                                ? '已逾期'
                                : '否',
                        },
                        {
                          key: 'sync',
                          label: '财务同步',
                          children: receiptFieldText(
                            'financeSyncStatus',
                            plan.financeSyncStatus,
                          ),
                        },
                      ]
                    : []),
                  ...(registration
                    ? [
                        {
                          key: 'no',
                          label: '登记编号',
                          children: registration.registrationNo,
                        },
                        {
                          key: 'date',
                          label: '到账日期',
                          children: registration.receiptDate,
                        },
                        {
                          key: 'payer',
                          label: '付款方',
                          children: registration.payerName,
                        },
                        {
                          key: 'method',
                          label: '付款方式',
                          children:
                            receiptStatusText[registration.receiptMethod],
                        },
                        {
                          key: 'serial',
                          label: '流水号',
                          children: registration.bankSerialNo || '—',
                        },
                        {
                          key: 'creator',
                          label: '登记人',
                          children: registration.creatorName || '—',
                        },
                        {
                          key: 'reason',
                          label: '驳回原因',
                          children: registration.rejectionReason || '—',
                        },
                        {
                          key: 'difference',
                          label: '付款方差异说明',
                          children: registration.payerDifferenceReason || '—',
                        },
                        {
                          key: 'sync',
                          label: '同步状态',
                          children: receiptFieldText(
                            'syncStatus',
                            registration.syncStatus,
                          ),
                        },
                      ]
                    : []),
                  {
                    key: 'financeNo',
                    label: '财务单号',
                    children:
                      registration?.financeObjectNo ||
                      transaction?.financeObjectNo ||
                      '—',
                  },
                  {
                    key: 'confirmed',
                    label: '确认时间',
                    children:
                      registration?.confirmedAt ||
                      transaction?.confirmedAt ||
                      '—',
                  },
                  {
                    key: 'remark',
                    label: '备注',
                    children: plan?.remark || registration?.remark || '—',
                  },
                ]}
              />
              {transaction && (
                <Button
                  onClick={async () => {
                    try {
                      const { url } = await getReceiptFinanceLink(
                        transaction.uuid,
                      );
                      window.open(url, '_blank', 'noopener,noreferrer');
                    } catch {
                      setError('财务单据链接暂不可用');
                    }
                  }}
                >
                  查看财务单据
                </Button>
              )}
              {transaction?.reversalOf && (
                <Alert
                  type="info"
                  title={`此交易冲正原交易：${transaction.reversalOf}`}
                />
              )}
              {registration?.voucherAttachmentUuids.length ? (
                <Space wrap>
                  {registration.voucherAttachmentUuids.map(
                    (attachmentUuid, index) => (
                      <Button
                        key={attachmentUuid}
                        onClick={async () => {
                          try {
                            const result = await getAttachmentPreviewUrl(
                              attachmentUuid,
                              {
                                bizType: 'receipt-voucher',
                                bizUuid: registration.contractUuid,
                              },
                            );
                            if (!/^https?:\/\//i.test(result.url))
                              throw new Error();
                            window.open(
                              result.url,
                              '_blank',
                              'noopener,noreferrer',
                            );
                          } catch {
                            setError('凭证预览失败');
                          }
                        }}
                      >
                        查看凭证 {index + 1}
                      </Button>
                    ),
                  )}
                </Space>
              ) : null}
              {(registration || transaction) && (
                <Table
                  size="small"
                  rowKey="planUuid"
                  pagination={false}
                  dataSource={
                    registration?.allocations || transaction?.allocations
                  }
                  columns={[
                    { title: '计划 UUID', dataIndex: 'planUuid' },
                    { title: '分配金额', dataIndex: 'amount' },
                  ]}
                  scroll={{ x: 'max-content' }}
                />
              )}
              {plan && (
                <>
                  <ProTable<ReceiptRegistration>
                    size="small"
                    rowKey="uuid"
                    headerTitle="关联登记"
                    search={false}
                    options={false}
                    pagination={{
                      showSizeChanger: true,
                      showTotal: (total) => `共 ${total} 条`,
                    }}
                    scroll={{ x: 'max-content' }}
                    columns={[
                      { title: '登记编号', dataIndex: 'registrationNo' },
                      { title: '金额', dataIndex: 'amount' },
                      {
                        title: '状态',
                        dataIndex: 'status',
                        render: (_, row) =>
                          receiptStatusText[row.status] || row.status,
                      },
                    ]}
                    request={async ({ current = 1, pageSize = 20 }) => {
                      const result = await queryReceiptRegistrations({
                        contractUuid: plan.contractUuid,
                        planUuid: plan.uuid,
                        page: current,
                        pageSize,
                        scope: 'all',
                      });
                      return {
                        data: result.items,
                        total: result.total,
                        success: true,
                      };
                    }}
                  />
                  <ProTable<ReceiptTransaction>
                    size="small"
                    rowKey="uuid"
                    headerTitle="实际回款"
                    search={false}
                    options={false}
                    pagination={{
                      showSizeChanger: true,
                      showTotal: (total) => `共 ${total} 条`,
                    }}
                    scroll={{ x: 'max-content' }}
                    columns={[
                      { title: '财务单号', dataIndex: 'financeObjectNo' },
                      { title: '金额', dataIndex: 'amount' },
                      { title: '确认时间', dataIndex: 'confirmedAt' },
                    ]}
                    request={async ({ current = 1, pageSize = 20 }) => {
                      const result = await queryReceiptTransactions({
                        contractUuid: plan.contractUuid,
                        planUuid: plan.uuid,
                        page: current,
                        pageSize,
                        scope: 'all',
                      });
                      return {
                        data: result.items,
                        total: result.total,
                        success: true,
                      };
                    }}
                  />
                  <Typography.Title level={5}>调整记录</Typography.Title>
                  <Table
                    size="small"
                    rowKey={(row, index) => `${row.at}:${index}`}
                    dataSource={plan.history || []}
                    columns={[
                      { title: '时间', dataIndex: 'at' },
                      { title: '操作人', dataIndex: 'actor' },
                      { title: '内容', dataIndex: 'description' },
                    ]}
                    scroll={{ x: 'max-content' }}
                  />
                </>
              )}
            </>
          )}
        </Spin>
      </Space>
    </BusinessModal>
  );
}
